import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Spinner,
  Heading,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  buildMerchSuccessSummary,
  clearMerchCheckoutSession,
  extractOrderDocumentId,
  readMerchCheckoutSession,
  buildMerchCheckoutSessionFromMetadata,
  submitMerchOrderFromSession,
} from '../utils/merchOrderCheckout';
import { orderOutcome, track } from '../utils/umami';

const TRACKED_RETURNS_KEY = 'cil.umami.paymentReturns';

// Un ritorno da Stripe si conta una volta sola, anche se la pagina viene ricaricata
const isFirstReturn = (paymentIntentId) => {
  try {
    const store = window.sessionStorage;
    const seen = JSON.parse(store.getItem(TRACKED_RETURNS_KEY) || '[]');
    if (seen.includes(paymentIntentId)) return false;
    store.setItem(
      TRACKED_RETURNS_KEY,
      JSON.stringify([...seen.slice(-19), paymentIntentId])
    );
    return true;
  } catch {
    return true;
  }
};

/** Statistiche dei pagamenti con redirect (PayPal, Satispay, 3-D Secure). */
const trackPaymentReturn = ({
  paymentIntentId,
  eventId,
  socioId,
  paymentType,
  status,
  redirectStatus,
  amount,
}) => {
  if (!paymentIntentId || !isFirstReturn(paymentIntentId)) return;
  let ambito = 'altro';
  if (paymentType === 'product') ambito = 'merch';
  else if (eventId) ambito = 'evento';
  else if (socioId) ambito = 'tessera';

  const succeeded = redirectStatus === 'succeeded' || status === 'succeeded';
  if (!succeeded && status !== 'processing') {
    track.paymentError({ ambito, codice: redirectStatus || status });
    return;
  }
  const outcome = orderOutcome({
    paymentMethod: 'elettronico',
    paymentStatus: succeeded ? 'succeeded' : 'processing',
    amount: amount != null ? amount / 100 : undefined,
  });
  if (ambito === 'evento') {
    track.bookingComplete({ event_id: eventId, ...outcome });
  } else if (ambito === 'tessera') {
    track.membershipComplete({ metodo: outcome.metodo, esito: outcome.esito });
  }
};

function PaymentResult() {
  const navigate = useNavigate();
  const { requestUserData } = useAuth();
  const [searchParams] = useSearchParams();
  const paymentIntentId = searchParams.get('payment_intent');
  const redirectStatus = searchParams.get('redirect_status');
  const paymentIntentClientSecret = searchParams.get(
    'payment_intent_client_secret'
  );

  const [loading, setLoading] = useState(true);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPaymentIntent = async () => {
      try {
        if (!paymentIntentId || !paymentIntentClientSecret) {
          throw new Error(
            'Parametri di pagamento mancanti. Assicurati di essere arrivato da Stripe dopo il pagamento.'
          );
        }

        const response = await axios.get('/api/payment-intent', {
          params: {
            pi: paymentIntentId,
            clientSecret: paymentIntentClientSecret,
          },
        });

        const {
          socioId,
          eventId,
          eventUrlTitle,
          metadata,
          status,
          amount,
          localPaymentIntentId,
        } = response.data;
        const paymentType = metadata?.type;

        setPaymentIntent(response.data);
        trackPaymentReturn({
          paymentIntentId,
          eventId,
          socioId,
          paymentType,
          status,
          redirectStatus,
          amount,
        });

        if (paymentType === 'product') {
          const isSuccessful =
            redirectStatus === 'succeeded' ||
            ['succeeded', 'processing'].includes(status);

          if (!isSuccessful) {
            setLoading(false);
            return;
          }

          const session =
            readMerchCheckoutSession() ||
            buildMerchCheckoutSessionFromMetadata(metadata, {
              socioId,
              localPaymentIntentId,
              amountCents: amount,
            });
          if (session?.localPaymentIntentId && localPaymentIntentId) {
            session.localPaymentIntentId = localPaymentIntentId;
          }

          const paymentData = {
            paymentIntentId,
            paymentStatus: status || redirectStatus || 'succeeded',
            localPaymentIntentId,
            createdAt: new Date().toISOString(),
          };

          let orderDocumentId = null;
          try {
            const orderResponse = await submitMerchOrderFromSession(
              session,
              paymentData
            );
            orderDocumentId = extractOrderDocumentId(orderResponse);
          } catch (orderError) {
            if (orderError.response?.data) {
              orderDocumentId = extractOrderDocumentId(orderError.response);
            }
            if (!orderDocumentId && orderError.response?.data) {
              console.error(
                'Merch order creation after redirect failed:',
                orderError
              );
            } else if (!orderError.response?.data) {
              console.error(
                'Merch order creation after redirect failed:',
                orderError
              );
            }
          }

          if (requestUserData) {
            requestUserData({ withLoading: false });
          }

          const summary = buildMerchSuccessSummary({
            session,
            metadata,
            amountCents: amount,
            paymentData: {
              ...paymentData,
              orderDocumentId,
            },
          });

          clearMerchCheckoutSession();
          navigate('/merch/ordine-confermato', {
            state: summary,
            replace: true,
          });
          return;
        }

        if (!eventId && socioId) {
          const isSuccessful =
            redirectStatus === 'succeeded' ||
            ['succeeded', 'processing'].includes(status);

          if (isSuccessful) {
            try {
              await axios.post('/api/ordini', {
                participantId: socioId,
                paymentIntentId,
                paymentStatus: status || redirectStatus || 'succeeded',
                paymentMethod: 'elettronico',
                amount: amount != null ? amount / 100 : 10,
                paymentIntents: localPaymentIntentId,
                year: new Date().getFullYear(),
                eventVariants: {
                  [socioId]: {
                    type: 'tesseramento',
                    priceOption: 'totale',
                    amount: amount != null ? amount / 100 : 10,
                    name: 'Tesseramento',
                  },
                },
              });
            } catch (orderError) {
              // Il webhook Stripe può aver già creato/aggiornato l'ordine
              console.error(
                'Creazione ordine tesseramento dopo redirect fallita:',
                orderError.response?.data || orderError.message
              );
            }

            if (requestUserData) {
              requestUserData({ withLoading: false });
            }
          }

          navigate(
            `/account/tesseramenti?id=${socioId}&redirect_status=${redirectStatus || status}&paymentIntentId=${paymentIntentId}`
          );
          return;
        }

        if (eventId && redirectStatus === 'succeeded') {
          navigate(
            `/event/${eventUrlTitle}?redirect_status=${redirectStatus}&paymentIntentId=${paymentIntentId}`
          );
        }
      } catch (err) {
        setError(err.message || 'Error retrieving payment intent');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentIntent();
  }, [
    paymentIntentId,
    paymentIntentClientSecret,
    redirectStatus,
    navigate,
    requestUserData,
  ]);

  if (loading) {
    return (
      <Box textAlign="center" mt={10}>
        <Spinner size="xl" />
        <Text mt={4} color="gray.600">
          Verifica del pagamento in corso...
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" mt={10} px={4}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle mr={2}>Errore nel pagamento:</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (!paymentIntent) {
    return (
      <Box textAlign="center" mt={10}>
        <Text>Tentativo di pagamento non disponibile.</Text>
      </Box>
    );
  }

  const { status, amount, currency, eventName, metadata } = paymentIntent;
  const isMerchFailed = metadata?.type === 'product';

  return (
    <Box
      maxW="lg"
      mx="auto"
      mt={10}
      textAlign="center"
      bg={useColorModeValue('white', 'gray.800')}
      p={8}
      rounded="lg"
      shadow="md"
    >
      <Heading as="h2" size="lg" mb={6}>
        {isMerchFailed ? 'Pagamento non completato' : 'Risultato del pagamento'}
      </Heading>

      {['succeeded', 'processing'].includes(status) && !isMerchFailed && (
        <Alert status="success" mb={4}>
          <AlertIcon />
          Pagamento completato!
        </Alert>
      )}
      {status === 'requires_payment_method' && (
        <Alert status="warning" mb={4}>
          <AlertIcon />
          Il pagamento non è andato a buon fine. Riprova.
        </Alert>
      )}
      {isMerchFailed && status === 'requires_payment_method' && (
        <Text mb={4} color="gray.600">
          Il pagamento con PayPal non è stato completato. Puoi riprovare dal
          checkout del prodotto.
        </Text>
      )}

      {!isMerchFailed && (
        <>
          <Box mb={4}>
            <Text fontWeight="bold">Totale:</Text>
            <Text>
              {amount && currency
                ? `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`
                : 'N/A'}
            </Text>
          </Box>
          {eventName && (
            <Box mb={4}>
              <Text fontWeight="bold">Nome Evento:</Text>
              <Text>{eventName}</Text>
            </Box>
          )}
        </>
      )}

      <Button
        colorScheme="blue"
        as={Link}
        to={isMerchFailed ? '/merch' : '/'}
        mt={2}
      >
        {isMerchFailed ? 'Torna al catalogo merch' : 'Torna alla Home'}
      </Button>
    </Box>
  );
}

export default PaymentResult;
